import { z } from 'zod';

/**
 * Esquemas de ENTRADA. La API valida con estos y el front arma sus formularios con los
 * mismos: un campo obligatorio no puede serlo en un lado y opcional en el otro.
 *
 * Mensajes en criollo, pensados para quien carga: la API los devuelve tal cual.
 */

const texto = (nombre: string, max = 200) =>
  z.string({ required_error: `Falta ${nombre}.` }).trim().min(1, `Falta ${nombre}.`).max(max, `${nombre} es demasiado largo.`);
const textoOpcional = (max = 500) =>
  z.string().trim().max(max).optional().nullable().transform((v) => (v ? v : null));

export const uuid = z.string().uuid('Identificador inválido.');
export const fecha = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha tiene que tener el formato AAAA-MM-DD.');
/** Dinero en la unidad mínima de la moneda: entero y no negativo. */
export const importe = z.number({ invalid_type_error: 'El importe tiene que ser un número.' })
  .int('El importe no lleva decimales.')
  .nonnegative('El importe no puede ser negativo.')
  .max(Number.MAX_SAFE_INTEGER);
export const enteroNoNegativo = z.number().int().nonnegative();
export const puntosBasicos = z.number().int().min(0).max(100_000);

export const metodoCalculo = z.enum(['SIN_INTERES', 'INTERES_SIMPLE', 'FRANCES', 'MANUAL']);
export const frecuencia = z.enum(['MENSUAL', 'QUINCENAL', 'SEMANAL']);
export const tipoMora = z.enum(['NINGUNA', 'PORCENTAJE_DIARIO', 'PORCENTAJE_MENSUAL', 'MONTO_FIJO_DIARIO', 'MONTO_FIJO_UNICO']);
export const destinoResto = z.enum(['ULTIMA', 'PRIMERA']);
export const tipoMovimientoStock = z.enum(['ENTRADA', 'SALIDA_VENTA', 'AJUSTE_POSITIVO', 'AJUSTE_NEGATIVO', 'DEVOLUCION']);

export const paginacion = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(1).max(200).default(50),
  buscar: z.string().trim().max(100).optional(),
});

// --- Sesión ------------------------------------------------------------------

/**
 * Se ingresa con lo que la persona recuerde: nombre de usuario, correo o cédula. No se
 * exige formato de correo: un comercio quiere usuarios cortos ("dexter", "caja1").
 */
export const esquemaLogin = z.object({
  usuario: z.string().trim().toLowerCase().min(1, 'Escribí tu usuario, correo o cédula.'),
  clave: z.string().min(1, 'Escribí tu contraseña.'),
  entidadId: uuid.optional(),
});

/** Nombre de usuario o correo: minúsculas, sin espacios. */
export const identificadorUsuario = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'El usuario tiene que tener al menos 3 caracteres.')
  .max(120)
  .regex(/^[a-z0-9._@+-]+$/, 'El usuario va sin espacios: letras, números, punto, guion o @.')
  .refine((v) => !/^\d+$/.test(v), 'El usuario no puede ser solo números: para eso está la cédula.');

/** Cédula: se guarda solo con dígitos ("1.234.567" → "1234567"). */
export const documentoUsuario = z
  .string()
  .trim()
  .transform((v) => v.replace(/[.\s-]/g, ''))
  .refine((v) => v === '' || /^\d{5,12}$/.test(v), 'La cédula tiene que tener entre 5 y 12 dígitos.')
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
export type EntradaLogin = z.infer<typeof esquemaLogin>;

export const esquemaCambioEntidad = z.object({ entidadId: uuid });

export const esquemaCambioClave = z.object({
  claveActual: z.string().min(1, 'Escribí tu contraseña actual.'),
  claveNueva: z.string().min(10, 'La contraseña nueva tiene que tener al menos 10 caracteres.'),
});

// --- Clientes ----------------------------------------------------------------

export const esquemaCliente = z.object({
  nombre: texto('el nombre'),
  documento: textoOpcional(30),
  telefono: textoOpcional(40),
  email: z.string().trim().email('El correo no es válido.').optional().nullable().or(z.literal('')).transform((v) => (v ? v : null)),
  direccion: textoOpcional(200),
  ciudad: textoOpcional(80),
  notas: textoOpcional(1000),
});
export type EntradaCliente = z.infer<typeof esquemaCliente>;

// --- Inventario --------------------------------------------------------------

export const esquemaCategoria = z.object({
  nombre: texto('el nombre', 80),
  orden: enteroNoNegativo.default(0),
  activo: z.boolean().default(true),
});

export const esquemaProducto = z.object({
  codigo: textoOpcional(40),
  nombre: texto('el nombre'),
  descripcion: textoOpcional(1000),
  categoriaId: uuid.optional().nullable(),
  precioContado: importe,
  costo: importe.default(0),
  controlaStock: z.boolean().default(true),
  stockMinimo: enteroNoNegativo.default(0),
  activo: z.boolean().default(true),
  /** Solo en el alta: existencias con las que arranca. */
  stockInicial: enteroNoNegativo.optional(),
});
export type EntradaProducto = z.infer<typeof esquemaProducto>;

export const esquemaMovimientoStock = z.object({
  tipo: z.enum(['ENTRADA', 'AJUSTE_POSITIVO', 'AJUSTE_NEGATIVO']),
  cantidad: z.number().int().positive('La cantidad tiene que ser mayor a cero.'),
  motivo: textoOpcional(300),
});

// --- Configuración de cobro ----------------------------------------------------

export const esquemaMedioPago = z.object({
  nombre: texto('el nombre', 60),
  requiereComprobante: z.boolean().default(false),
  requiereReferencia: z.boolean().default(false),
  orden: enteroNoNegativo.default(0),
  activo: z.boolean().default(true),
});

export const esquemaReglaMora = z.object({
  tipo: tipoMora,
  valor: enteroNoNegativo,
  diasGracia: enteroNoNegativo.max(365),
  topeBp: puntosBasicos.default(0),
});

export const esquemaPlanFinanciacion = z.object({
  nombre: texto('el nombre', 80),
  descripcion: textoOpcional(300),
  cantidadCuotas: z.number().int().min(1).max(240),
  frecuencia,
  metodo: metodoCalculo,
  tasaBp: puntosBasicos.default(0),
  entregaSugeridaBp: z.number().int().min(0).max(10_000).default(0),
  mora: esquemaReglaMora,
  predeterminado: z.boolean().default(false),
  orden: enteroNoNegativo.default(0),
  activo: z.boolean().default(true),
});
export type EntradaPlanFinanciacion = z.infer<typeof esquemaPlanFinanciacion>;

export const esquemaEntidad = z.object({
  nombre: texto('el nombre', 120),
  razonSocial: textoOpcional(200),
  ruc: textoOpcional(30),
  direccion: textoOpcional(200),
  telefono: textoOpcional(40),
  email: textoOpcional(120),
  zonaHoraria: z.string().min(1).refine(esZonaValida, 'La zona horaria no existe.'),
  moneda: z.enum(['PYG', 'USD', 'BRL', 'ARS']),
  colorMarca: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'El color tiene que ser hexadecimal, como #3450A1.').optional().nullable(),
  unidadRedondeo: z.number().int().min(1).max(100_000),
  destinoResto,
  diasAviso: z.number().int().min(0).max(60),
  prefijoRecibo: z.string().trim().min(1).max(8),
  pieRecibo: textoOpcional(500),
});

function esZonaValida(zona: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zona });
    return true;
  } catch {
    return false;
  }
}

// --- Ventas -------------------------------------------------------------------

export const esquemaLineaVenta = z.object({
  /** Opcional: la venta no depende de que el producto esté en el inventario. */
  productoId: uuid.optional().nullable(),
  codigo: textoOpcional(40),
  descripcion: texto('la descripción del producto', 300),
  cantidad: z.number().int().positive('La cantidad tiene que ser mayor a cero.'),
  precioUnitario: importe,
  /** Con producto de inventario: si descuenta existencias. */
  descontarStock: z.boolean().default(true),
});

export const esquemaFinanciacion = z.object({
  planFinanciacionId: uuid.optional().nullable(),
  entregaInicial: importe.default(0),
  cantidadCuotas: z.number().int().min(1).max(240),
  metodo: metodoCalculo,
  tasaBp: puntosBasicos.default(0),
  frecuencia,
  primerVencimiento: fecha,
  unidadRedondeo: z.number().int().min(1).max(100_000).default(1),
  destinoResto: destinoResto.default('ULTIMA'),
  montosManuales: z.array(importe).optional(),
  vencimientosManuales: z.array(fecha).optional(),
  mora: esquemaReglaMora,
});
export type EntradaFinanciacion = z.infer<typeof esquemaFinanciacion>;

export const esquemaVenta = z.object({
  clienteId: uuid,
  fecha,
  observaciones: textoOpcional(1000),
  lineas: z.array(esquemaLineaVenta).min(1, 'La venta tiene que tener al menos un producto.').max(100),
  financiacion: esquemaFinanciacion,
  /** Si la entrega inicial se cobra en el momento, se registra el pago con su recibo. */
  cobroEntrega: z
    .object({
      medioPagoId: uuid,
      referencia: textoOpcional(100),
      comprobanteId: uuid.optional().nullable(),
    })
    .optional()
    .nullable(),
});
export type EntradaVenta = z.infer<typeof esquemaVenta>;

export const esquemaAnulacion = z.object({
  motivo: texto('el motivo de la anulación', 500),
});

export const esquemaReprogramacion = z.object({
  vencimiento: fecha,
  motivo: texto('el motivo', 300),
});

// --- Cobranzas ------------------------------------------------------------------

export const esquemaAplicacion = z.object({
  cuotaId: uuid,
  montoCuota: importe,
  montoMora: importe,
  moraCondonada: importe,
});

export const esquemaPropuestaPago = z.object({
  ventaId: uuid,
  fecha,
  monto: importe,
  cobrarMora: z.boolean().default(true),
});

export const esquemaPago = z.object({
  ventaId: uuid,
  fecha,
  monto: importe,
  medioPagoId: uuid,
  referencia: textoOpcional(100),
  observaciones: textoOpcional(500),
  comprobanteId: uuid.optional().nullable(),
  /** Si no viene, se aplica la propuesta automática (mora primero, cuota más vieja). */
  aplicaciones: z.array(esquemaAplicacion).optional(),
  cobrarMora: z.boolean().default(true),
});
export type EntradaPago = z.infer<typeof esquemaPago>;

// --- Facturas ---------------------------------------------------------------------

export const esquemaFactura = z.object({
  clienteId: uuid.optional().nullable(),
  ventaId: uuid.optional().nullable(),
  timbrado: z.string().trim().regex(/^\d{8}$/, 'El timbrado tiene 8 dígitos.'),
  numero: z.string().trim().regex(/^\d{3}-\d{3}-\d{7}$/, 'El número tiene el formato 001-001-0000123.'),
  fechaEmision: fecha,
  condicion: z.enum(['CONTADO', 'CREDITO']),
  montoGravado10: importe.default(0),
  montoGravado5: importe.default(0),
  montoExento: importe.default(0),
  archivoId: uuid.optional().nullable(),
  observaciones: textoOpcional(500),
});
export type EntradaFactura = z.infer<typeof esquemaFactura>;

// --- Usuarios y roles ---------------------------------------------------------------

export const esquemaRol = z.object({
  nombre: texto('el nombre', 60),
  descripcion: textoOpcional(300),
  permisos: z.array(z.string()).max(200),
  activo: z.boolean().default(true),
});

export const esquemaAltaUsuario = z.object({
  email: identificadorUsuario,
  documento: documentoUsuario,
  nombre: texto('el nombre', 120),
  rolId: uuid,
  /** Solo si la persona todavía no tiene cuenta en Libreta. */
  clave: z.string().min(10, 'La contraseña tiene que tener al menos 10 caracteres.').optional(),
});

export const esquemaEdicionMiembro = z.object({
  rolId: uuid.optional(),
  activo: z.boolean().optional(),
  documento: documentoUsuario,
});

export const esquemaRestablecerClave = z.object({
  clave: z.string().min(10, 'La contraseña tiene que tener al menos 10 caracteres.'),
});

// --- Auditoría ----------------------------------------------------------------------

export const esquemaFiltroAuditoria = paginacion.extend({
  tabla: z.string().max(60).optional(),
  registroId: z.string().max(60).optional(),
  actorId: z.string().max(60).optional(),
  tipo: z.enum(['CAMBIO', 'EVENTO']).optional(),
  desde: fecha.optional(),
  hasta: fecha.optional(),
});

// --- Plataforma -----------------------------------------------------------------------

export const esquemaAltaEntidad = z.object({
  nombre: texto('el nombre', 120),
  slug: z.string().trim().toLowerCase().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'El identificador va en minúsculas, con guiones.'),
  planId: uuid.optional().nullable(),
  zonaHoraria: z.string().default('America/Asuncion'),
  moneda: z.enum(['PYG', 'USD', 'BRL', 'ARS']).default('PYG'),
  administrador: z.object({
    email: identificadorUsuario,
    documento: documentoUsuario,
    nombre: texto('el nombre', 120),
    clave: z.string().min(10, 'La contraseña tiene que tener al menos 10 caracteres.').optional(),
  }),
});

export const esquemaEdicionEntidadPlataforma = z.object({
  estado: z.enum(['ACTIVA', 'SUSPENDIDA', 'BAJA']).optional(),
  planId: uuid.optional().nullable(),
});

export const esquemaBanderaModulo = z.object({
  habilitado: z.boolean(),
  nota: texto('la nota que explica el cambio', 300),
});

export const esquemaPlanComercial = z.object({
  nombre: texto('el nombre', 60),
  descripcion: textoOpcional(300),
  modulos: z.array(z.string()),
  precioMensual: importe,
  moneda: z.enum(['PYG', 'USD']).default('PYG'),
  activo: z.boolean().default(true),
  orden: enteroNoNegativo.default(0),
});
